import { useState, useCallback, useRef } from "react";

export interface ChatMessageAction {
  label: string;
  icon: string;
  key: string;
}

export interface ChatMessage {
  id: string;
  role: "user" | "assistant";
  content: string;
  timestamp: number;
  actions?: ChatMessageAction[];
  brainAction?: {
    type: string;
    payload: Record<string, unknown>;
  } | null;
  metadata?: {
    total_facts_in_db?: number;
    eligible_after_level_filter?: number;
    fact_ids?: { id: string; short: string }[];
    profile_loaded?: boolean;
  };
}

const STORAGE_KEY = "central-brain-history";
const MAX_MESSAGES = 100;

function loadMessages(): ChatMessage[] {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed.slice(-MAX_MESSAGES) : [];
  } catch {
    return [];
  }
}

function saveMessages(messages: ChatMessage[]) {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(messages.slice(-MAX_MESSAGES)));
    window.dispatchEvent(new Event("brain-history-changed"));
  } catch { /* quota exceeded — silent */ }
}

export function useChatHistory() {
  const [messages, setMessages] = useState<ChatMessage[]>(loadMessages);
  const messagesRef = useRef(messages);
  messagesRef.current = messages;

  const addMessage = useCallback((msg: Omit<ChatMessage, "id" | "timestamp">) => {
    const newMsg: ChatMessage = {
      ...msg,
      id: crypto.randomUUID(),
      timestamp: Date.now(),
    };
    setMessages((prev) => {
      const next = [...prev, newMsg].slice(-MAX_MESSAGES);
      saveMessages(next);
      return next;
    });
    return newMsg.id;
  }, []);

  const updateLastAssistant = useCallback((content: string, metadata?: ChatMessage["metadata"], brainAction?: ChatMessage["brainAction"]) => {
    setMessages((prev) => {
      const idx = prev.length - 1;
      if (idx < 0 || prev[idx].role !== "assistant") return prev;
      const next = [...prev];
      next[idx] = {
        ...next[idx],
        content,
        ...(metadata ? { metadata } : {}),
        ...(brainAction !== undefined ? { brainAction } : {}),
      };
      saveMessages(next);
      return next;
    });
  }, []);

  const loadFromDb = useCallback((dbMessages: Array<{ role: string; content: string; action?: any; created_at: string }>) => {
    const mapped: ChatMessage[] = dbMessages.map((m) => ({
      id: crypto.randomUUID(),
      role: m.role === "cerveau" ? "assistant" as const : "user" as const,
      content: m.content,
      timestamp: new Date(m.created_at).getTime(),
      brainAction: m.action || null,
    }));
    setMessages(mapped);
    saveMessages(mapped);
  }, []);

  const clearHistory = useCallback(() => {
    setMessages([]);
    localStorage.removeItem(STORAGE_KEY);
    window.dispatchEvent(new Event("brain-history-changed"));
  }, []);

  return { messages, addMessage, updateLastAssistant, loadFromDb, clearHistory };
}
